"use client";

import type { Breadcrumb, ErrorEvent } from "@sentry/react";
import posthog, { type CaptureResult } from "posthog-js";
import { FREE_TEST_PAGES } from "./constants";
import { isLocale } from "./i18n/locales";
import { interactionErrorContext, interactionPlaying, onInteractionPlaying } from "./testInteractions";
import type { WebsiteSentryConfig } from "./websiteSentryConfig.server";
import { WEBSITE_BOT, WEBSITE_EVENTS_PER_MINUTE, WEBSITE_REQUEST_ID, WEBSITE_SLOW_REQUEST_MS } from "./websiteErrorPolicy";

const RELEASE = process.env.NEXT_PUBLIC_WEBSITE_RELEASE || "local";
const BENIGN = /ResizeObserver loop (?:limit exceeded|completed with undelivered notifications)|(?:chrome|moz|safari)-extension:\/\/|^Script error\.?$|ERR_BLOCKED_BY_CLIENT/i;
const ERROR_TYPES = new Set(["Error", "TypeError", "ReferenceError", "SyntaxError", "RangeError", "URIError", "EvalError", "AggregateError", "WebsiteRequestError", "WebsiteMediaError"]);
const SAFE_TAGS = new Set(["website", "page_path", "test_code", "locale", "phase", "round", "device_class", "browser", "build_sha", "posthog_visitor_id", "request_id", "endpoint", "status", "latency_ms", "reason"]);
type Tags = Record<string, string | number>;
type Pending = { error: unknown; tags: Tags; fingerprint?: string[]; breadcrumbs: Breadcrumb[] };
let Sentry: typeof import("@sentry/react") | undefined;
let started = false;
let ready = false;
let scheduleSentry: (() => void) | undefined;
let pending: Pending[] = [];
const breadcrumbs: Breadcrumb[] = [];
const seen = new WeakSet<object>();
let quotaWindow = 0;
let quotaCount = 0;

export function isLoggingBot(): boolean {
  return navigator.webdriver === true || WEBSITE_BOT.test(navigator.userAgent);
}

/** Only l/v enum values survive. Never retain fragments, credentials or arbitrary query values. */
export function loggingUrl(raw: string): string {
  try {
    const url = new URL(raw, window.location.origin);
    if (!/^https?:$/.test(url.protocol)) return "[local-resource]";
    const path = url.pathname.split("/").map(part =>
      part.length <= 100 && /^[a-zA-Z0-9_.@()-]*$/.test(part) && !part.includes("@") ? part : "[redacted]",
    ).join("/");
    const query = new URLSearchParams();
    for (const key of ["l", "v"]) {
      const value = url.searchParams.get(key);
      if (value && /^[a-zA-Z0-9_-]{1,16}$/.test(value)) query.set(key, value);
    }
    return `${url.origin}${path}${query.size ? `?${query}` : ""}`;
  } catch {
    return "[invalid-url]";
  }
}

function visitorId(): string | undefined {
  if (!(posthog as unknown as { __loaded?: boolean }).__loaded) return;
  const id = posthog.get_distinct_id();
  // An anonymous SDK UUID only. An identify() call must never send an email/account value here.
  return typeof id === "string" && /^[a-f0-9-]{16,64}$/i.test(id) ? id : undefined;
}

export function websiteErrorContext(): Tags {
  const parts = window.location.pathname.split("/").filter(Boolean);
  if (parts[0] === "embed") parts.shift();
  const locale = isLocale(parts[0]) ? parts.shift()! : "en";
  const path = `/${parts.join("/")}`;
  const test = FREE_TEST_PAGES.find(page => page.href === path)?.test;
  const tool = parts[0] === "tools" && /^[a-z0-9-]{1,64}$/.test(parts[1] || "") ? parts[1].replaceAll("-", "_") : undefined;
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua) ? "Edge" : /Firefox\//.test(ua) ? "Firefox" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "other";
  const visitor = visitorId();
  return {
    website: "true", page_path: new URL(loggingUrl(window.location.href)).pathname,
    test_code: test || tool || (path === "/" ? "website" : parts.join("_").replace(/[^a-z0-9_-]/g, "").slice(0, 64)) || "website", locale, browser, build_sha: RELEASE,
    device_class: /Mobi|Android|iPhone|iPad/i.test(ua) ? (window.innerWidth >= 768 ? "tablet" : "mobile") : "desktop",
    ...(visitor ? { posthog_visitor_id: visitor } : {}),
    ...interactionErrorContext(),
  };
}

/** Rebuild before the PostHog processor as well as before Sentry transport. Neither collector
 * receives raw messages, input values, DOM selectors, console arguments, bodies, locals or headers. */
export function scrubWebsiteEvent(event: ErrorEvent, enforceQuota = true): ErrorEvent | null {
  if (isLoggingBot()) return null;
  if (event.exception?.values?.some(value => BENIGN.test(value.value || "") ||
    value.stacktrace?.frames?.some(frame => /-extension:\/\//.test(frame.filename || "")))) return null;
  const now = Date.now();
  if (now - quotaWindow >= 60_000) { quotaWindow = now; quotaCount = 0; }
  if (enforceQuota && ++quotaCount > WEBSITE_EVENTS_PER_MINUTE) return null;
  const tags: Tags = {};
  for (const [key, value] of Object.entries({ ...websiteErrorContext(), ...event.tags })) {
    if (SAFE_TAGS.has(key) && (typeof value === "string" || typeof value === "number")) tags[key] = String(value).slice(0, 200);
  }
  if (!/^[a-f0-9-]{16,64}$/i.test(String(tags.posthog_visitor_id || ""))) delete tags.posthog_visitor_id;
  const person = event.tags?.["PostHog Person URL"];
  const visitor = visitorId();
  if (visitor && posthog.config?.ui_host && person === `${posthog.config.ui_host.replace(/\/$/, "")}/project/${posthog.config.token}/person/${visitor}`) {
    tags["PostHog Person URL"] = person;
  }
  return {
    type: undefined, event_id: event.event_id, timestamp: event.timestamp, platform: "javascript", level: event.level || "error",
    environment: event.environment, release: RELEASE,
    ...(event.fingerprint ? { fingerprint: event.fingerprint } : {}),
    tags, user: { ...(tags.posthog_visitor_id ? { id: String(tags.posthog_visitor_id) } : {}), ip_address: "0.0.0.0" },
    request: { url: loggingUrl(window.location.href) },
    contexts: { website: { viewport_width: window.innerWidth, viewport_height: window.innerHeight } },
    exception: { values: event.exception?.values?.map(value => ({
      type: ERROR_TYPES.has(value.type || "") ? value.type : "Error",
      value: `${ERROR_TYPES.has(value.type || "") ? value.type : "Error"} in website code`,
      mechanism: value.mechanism ? { type: value.mechanism.type, handled: value.mechanism.handled } : undefined,
      stacktrace: value.stacktrace ? { frames: value.stacktrace.frames?.map(frame => ({
        filename: frame.filename ? loggingUrl(frame.filename) : undefined,
        function: frame.function?.slice(0, 160), lineno: frame.lineno, colno: frame.colno, in_app: frame.in_app,
      })) } : undefined,
    })) },
    breadcrumbs: event.breadcrumbs?.map(scrubBreadcrumb).filter((item): item is Breadcrumb => !!item).slice(-40),
  };
}

function scrubBreadcrumb(item: Breadcrumb): Breadcrumb | null {
  const base = { category: item.category, level: item.level, timestamp: item.timestamp };
  if (item.category === "website.request") return { ...base,
    data: Object.fromEntries(Object.entries(item.data || {}).filter(([key]) => SAFE_TAGS.has(key))),
  };
  if (item.category === "ui.click") return { ...base, message: /^(button|a|input|textarea|select|summary|canvas|div|span|svg|other)$/.test(item.message || "") ? item.message : "other" };
  if (item.category === "console") {
    const method = ["debug", "info", "log", "warn", "error", "assert"].includes(item.data?.method) ? item.data!.method : "log";
    return { ...base, message: `console.${method}`, data: { method } };
  }
  if (item.category === "navigation") return { ...base, data: {
    from: loggingUrl(String(item.data?.from || "/")), to: loggingUrl(String(item.data?.to || "/")),
  } };
  if (item.category === "fetch") return { ...base, data: {
    url: loggingUrl(String(item.data?.url || "/")),
    method: /^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)$/.test(item.data?.method) ? item.data!.method : "GET",
    status_code: Number.isInteger(item.data?.status_code) ? item.data!.status_code : 0,
  } };
  return null;
}

function addBreadcrumb(item: Breadcrumb): void {
  const clean = scrubBreadcrumb({ ...item, timestamp: Date.now() / 1000 });
  if (clean) { breadcrumbs.push(clean); if (breadcrumbs.length > 40) breadcrumbs.shift(); }
}

/** PostHog adds URL/referrer/person metadata after its Sentry processor. Keep only the sanitized
 * exception and SDK routing fields; ordinary click/product analytics retain their existing policy. */
export function scrubPostHogException(event: CaptureResult | null): CaptureResult | null {
  if (!event || event.event !== "$exception") return event;
  const allowed = new Set(["token", "distinct_id", "$device_id", "$session_id", "$window_id", "$lib", "$lib_version",
    "$exception_message", "$exception_type", "$exception_level", "$exception_list", "$sentry_event_id", "$sentry_exception",
    "$sentry_exception_message", "$sentry_exception_type", "$sentry_tags", "$sentry_url"]);
  return { ...event, properties: Object.fromEntries(Object.entries(event.properties).filter(([key]) => allowed.has(key))), $set: undefined, $set_once: undefined };
}

function sendException(item: Pending): void {
  if (!Sentry) return;
  Sentry.withScope(scope => {
    scope.clearBreadcrumbs();
    for (const breadcrumb of item.breadcrumbs) scope.addBreadcrumb(breadcrumb);
    Sentry!.captureException(item.error, { tags: item.tags, fingerprint: item.fingerprint });
  });
}

export function captureWebsiteException(error: unknown, tags: Tags = {}, fingerprint?: string[]): void {
  if (typeof window === "undefined" || isLoggingBot()) return;
  if (error instanceof Error && BENIGN.test(`${error.message}\n${error.stack || ""}`)) return;
  if (error && typeof error === "object") {
    if (seen.has(error)) return;
    seen.add(error);
  }
  // Snapshot page/phase before navigation or recovery changes the current game.
  const item = { error: error instanceof Error ? error : new Error("Unhandled website rejection"), tags: { ...websiteErrorContext(), ...tags }, fingerprint, breadcrumbs: breadcrumbs.slice() };
  if (!ready || interactionPlaying()) {
    if (pending.length < WEBSITE_EVENTS_PER_MINUTE) pending.push(item);
    if (!ready && !interactionPlaying()) scheduleSentry?.();
    return;
  }
  sendException(item);
}

function requestId(): string {
  return crypto.randomUUID();
}

/** One event per fetch, even when it is both slow and fails. No request/response body is read. */
export function observeWebsiteRequests(original: typeof fetch): typeof fetch {
  return async (input, init) => {
    const raw = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin || !url.pathname.startsWith("/free_tests/")) {
      const method = init?.method || (input instanceof Request ? input.method : "GET");
      try {
        const response = await original(input, init);
        addBreadcrumb({ category: "fetch", data: { url: raw, method, status_code: response.status } });
        return response;
      } catch (error) {
        addBreadcrumb({ category: "fetch", level: "error", data: { url: raw, method, status_code: 0 } });
        throw error;
      }
    }
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    const id = requestId();
    headers.set("X-Request-ID", id);
    const tags: Tags = { ...websiteErrorContext(), endpoint: new URL(loggingUrl(url.href)).pathname, request_id: id };
    const start = performance.now();
    let reported = false;
    const report = (reason: string, status: number | string, responseId = id) => {
      if (reported) return;
      reported = true;
      const error = new Error("Website API request failed or exceeded five seconds");
      error.name = "WebsiteRequestError";
      captureWebsiteException(error, { ...tags, reason, status, latency_ms: Math.round(performance.now() - start), request_id: WEBSITE_REQUEST_ID.test(responseId) ? responseId : id },
        ["website.request", String(tags.endpoint), String(status), reason]);
    };
    const timer = setTimeout(() => report("slow", "pending"), WEBSITE_SLOW_REQUEST_MS + 1);
    try {
      const response = await original(input, { ...init, headers });
      const elapsed = Math.round(performance.now() - start);
      addBreadcrumb({ category: "website.request", level: response.status >= 400 ? "error" : "info", data: { ...tags, status: response.status, latency_ms: elapsed } });
      if (response.status >= 400 || elapsed > WEBSITE_SLOW_REQUEST_MS) report(response.status >= 400 ? "http" : "slow", response.status, response.headers.get("X-Request-ID") || id);
      return response;
    } catch (error) {
      report(error instanceof DOMException && error.name === "AbortError" ? "aborted" : "network", 0);
      // The same rejection can later reach the global handler; this request already has its event.
      if (error && typeof error === "object") seen.add(error);
      throw error;
    } finally {
      clearTimeout(timer);
    }
  };
}

export function startWebsiteErrorLogging(): void {
  if (started || isLoggingBot()) return;
  started = true;
  // Categorical breadcrumbs work before the lazy SDK arrives. Never inspect input values,
  // text, selectors or console arguments. Timed clicks already belong to the V1.13 game log.
  window.addEventListener("click", event => {
    if (interactionPlaying()) return;
    addBreadcrumb({ category: "ui.click", message: event.target instanceof Element ? event.target.tagName.toLowerCase() : "other" });
  }, { capture: true, passive: true });
  for (const method of ["debug", "info", "log", "warn", "error", "assert"] as const) {
    const original = console[method];
    console[method] = (...args: unknown[]) => {
      addBreadcrumb({ category: "console", level: method === "warn" ? "warning" : method === "error" || method === "assert" ? "error" : "info", data: { method } });
      Reflect.apply(original, console, args);
    };
  }
  let previousUrl = window.location.href;
  const navigation = () => {
    addBreadcrumb({ category: "navigation", data: { from: previousUrl, to: window.location.href } });
    previousUrl = window.location.href;
  };
  for (const method of ["pushState", "replaceState"] as const) {
    const original = window.history[method];
    window.history[method] = (...args: Parameters<History[typeof method]>) => {
      original.apply(window.history, args); navigation();
    };
  }
  window.addEventListener("popstate", navigation);
  // Install before fetching runtime configuration, including early unhandled rejections.
  window.addEventListener("error", event => {
    if (event.target === window) captureWebsiteException(event.error || new Error(event.message));
    else if (event.target instanceof HTMLImageElement || event.target instanceof HTMLMediaElement || event.target instanceof HTMLLinkElement) {
      if (websiteErrorContext().test_code === "website") return;
      const target = event.target;
      const source = target instanceof HTMLLinkElement ? target.href : target.currentSrc || target.src;
      const url = loggingUrl(source);
      if (!/^https?:/.test(url) || new URL(url).origin !== window.location.origin) return;
      const error = new Error("Required website media failed to load");
      error.name = "WebsiteMediaError";
      captureWebsiteException(error, { endpoint: url, reason: target.tagName.toLowerCase() }, ["website.media", url]);
    }
  }, true);
  window.addEventListener("unhandledrejection", event => captureWebsiteException(event.reason));
  document.fonts?.addEventListener("loadingerror", event => {
    // Next/font probes optional local fallback faces (e.g. Arial on Linux). Their absence
    // is harmless when the downloaded font loads, and must not create an issue per visitor.
    const faces = (event as FontFaceSetLoadEvent).fontfaces || [];
    if (faces.length && faces.every(face => /\bFallback$/i.test(face.family.replace(/["']/g, "")))) return;
    if (websiteErrorContext().test_code !== "website") captureWebsiteException(new Error("Website font failed to load"), { reason: "font" }, ["website.media", "font"]);
  });
  const original = window.fetch.bind(window);
  window.fetch = observeWebsiteRequests(original);
  let config: WebsiteSentryConfig | undefined;
  let loading = false;
  const idle = (callback: () => void) => {
    if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(callback);
    else setTimeout(callback, 0);
  };
  const initialise = () => {
    if (!config || loading || ready || interactionPlaying()) return;
    loading = true;
    void import("@sentry/react").then(sdk => {
      Sentry = sdk;
      if (interactionPlaying()) { loading = false; return; }
      posthog.set_config({ ui_host: config!.posthogUiHost });
      const posthogIntegration = posthog.sentryIntegration({
        organization: config!.sentryOrganization, projectId: config!.sentryProjectId,
        severityAllowList: "*", sendExceptionsToPostHog: true,
      });
      const processPostHogEvent = posthogIntegration.processEvent;
      posthogIntegration.processEvent = event => visitorId() ? processPostHogEvent(event) : event;
      sdk.init({
        dsn: config!.dsn, environment: config!.environment, release: RELEASE,
        dataCollection: {
          userInfo: false, cookies: false, httpHeaders: false, httpBodies: [],
          urlQueryParams: { allow: ["l", "v"] }, stackFrameVariables: false, frameContextLines: 0,
          graphQL: { document: false, variables: false }, genAI: { inputs: false, outputs: false },
          mcp: { inputs: false, outputs: false }, databaseQueryData: false, queues: false,
        },
        sampleRate: 1, tracesSampleRate: 0, profileSessionSampleRate: 0,
        replaysSessionSampleRate: 0, replaysOnErrorSampleRate: 0,
        // Our bounded categorical breadcrumbs avoid DOM/console serialization. Sanitize BEFORE
        // the official integration forwards to PostHog; beforeSend runs after event processors.
        integrations: defaults => [
          ...defaults.filter(integration => !["Breadcrumbs", "Console", "GlobalHandlers", "BrowserApiErrors", "HttpContext", "Dedupe", "BrowserSession"].includes(integration.name)),
          { name: "WebsitePrivacy", processEvent: event => event.type ? null : scrubWebsiteEvent(event as ErrorEvent) },
          posthogIntegration,
        ],
        beforeSend: event => scrubWebsiteEvent(event, false),
      });
      ready = true;
      const queued = pending; pending = [];
      for (const item of queued) sendException(item);
    }).catch(failed);
  };
  const failed = () => {
    console.error("Website error reporting could not initialise");
    pending = [];
  };
  scheduleSentry = () => idle(initialise);
  onInteractionPlaying(playing => {
    if (playing) return;
    idle(() => {
      if (interactionPlaying()) return;
      if (!ready) { if (pending.length) initialise(); }
      else { const queued = pending; pending = []; for (const item of queued) sendException(item); }
    });
  });
  void original("/.well-known/website-error-config", { cache: "no-store", credentials: "omit" })
    .then(async response => {
      if (!response.ok) throw new Error("Website logging configuration unavailable");
      const value = await response.json() as WebsiteSentryConfig;
      if (!value.dsn || !["prod", "dev_robert"].includes(value.environment || "")) { pending = []; return; }
      config = value;
      // A normal test run downloads and initializes no Sentry SDK. The small recorder above
      // already has its breadcrumbs when an actual error needs reporting after a run.
      if (pending.length) idle(initialise);
    })
    .catch(failed);
}
